import { useQuery } from '@tanstack/react-query'
import { useDeferredValue, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { fetchPage } from '@/lists/api'

/**
 * Выбор записи по названию (тикет #100): клиента сделки, товара позиции.
 *
 * Ищет через механизм списков (#48), а не своим эндпоинтом: список уже сужен областью
 * видимости и полевыми правами, и второй путь чтения означал бы второе место, где решается,
 * кто какие записи видит. Поле для идентификатора на его месте заставляло бы человека
 * копировать UUID из адресной строки — то есть не давало бы выбрать вовсе.
 */
export function RecordPicker({
  id,
  entity,
  searchColumn,
  extraColumn,
  value,
  onChange,
}: {
  id: string
  entity: string
  /** Колонка, по которой ищут и которую показывают (`name`). */
  searchColumn: string
  /** Уточнение рядом с названием: ИНН клиента, артикул товара. */
  extraColumn?: string
  value: { id: string; label: string } | null
  onChange: (value: { id: string; label: string } | null) => void
}) {
  const { t } = useApp()
  const [text, setText] = useState('')
  const query = useDeferredValue(text.trim())

  const found = useQuery({
    queryKey: ['record-picker', entity, searchColumn, query],
    enabled: value === null && query.length >= 2,
    queryFn: ({ signal }) =>
      fetchPage(
        entity,
        {
          columns: extraColumn ? [searchColumn, extraColumn] : [searchColumn],
          filters: [{ column: searchColumn, operator: 'contains', value: query }],
          sort: [{ column: searchColumn, descending: false }],
          pageSize: 10,
        },
        null,
        signal,
      ),
  })

  if (value) {
    return (
      <span>
        {value.label}{' '}
        <button type="button" onClick={() => onChange(null)} aria-label={t('picker.clear')}>
          ×
        </button>
      </span>
    )
  }

  const rows = found.data?.rows ?? []
  return (
    <span className="record-picker">
      <input
        id={id}
        value={text}
        placeholder={t('picker.search')}
        onChange={(event) => setText(event.target.value)}
      />
      {/* Отказ поиска (нет права на справочник) — словами, а не пустым списком: пустота
          читалась бы как «таких записей нет». */}
      {found.isError && <span className="muted" role="alert"> {t('picker.failed')}</span>}
      {query.length >= 2 && found.isSuccess && rows.length === 0 && (
        <span className="muted"> {t('picker.nothing')}</span>
      )}
      {rows.length > 0 && (
        <ul role="listbox" aria-label={t('picker.results')}>
          {rows.map((row) => {
            const label = String(row[searchColumn] ?? row.id)
            const extra = extraColumn && row[extraColumn] ? ` · ${String(row[extraColumn])}` : ''
            return (
              <li key={row.id}>
                <button type="button" role="option" aria-selected={false}
                        onClick={() => onChange({ id: row.id, label })}>
                  {label}
                  <span className="muted">{extra}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </span>
  )
}
